/**
 * scene_loader.js – Cesium scene loading logic.
 *
 * Registers window.loadScene for backward compatibility.
 *
 * Dependencies (all available as globals or imports):
 *   Cesium, axios, window.viewer, window.dropNamedAsset,
 *   showNotification, validateITwinConfig, hideITwinHeightSlider
 */

import axios from 'axios';
import { getBaseUrl, getCloudfront } from '../../envUtils';
import { waitForViewerReady } from './cesium_utils';
import { validateITwinConfig, hideITwinHeightSlider } from './itwin_scene';
import { getEnvType } from './scene_api';

// Notification helper — log only, do NOT call window.showAlert as it triggers
// Redux state changes that cause TerrainApp to remount and wipe the Cesium scene
const showNotification = (type, message) => {
    console.log(`[${type.toUpperCase()}] ${message}`);
};

// ---------------------------------------------------------------------------
// Camera helper
// ---------------------------------------------------------------------------

/**
 * Fly the Cesium camera to a position above the scene coords.
 * @param {{ latitude: number, longitude: number }} coords  - degrees
 * @param {number} terrainHeight                            - metres
 */
const flyCamera = (coords, terrainHeight) => {
    const cameraPosition = Cesium.Cartesian3.fromDegrees(
        coords.longitude,
        coords.latitude,
        terrainHeight + 200
    );
    window.viewer.camera.flyTo({
        destination: cameraPosition,
        orientation: {
            heading: Cesium.Math.toRadians(0.0),
            pitch:   Cesium.Math.toRadians(-30.0),
            roll:    Cesium.Math.toRadians(0.0)
        }
    });

    // Offset the camera SW of the building for a nicer angle
    const OFFSET_DIST = 500;
    const OFFSET_H    = 150;
    const bearing     = Cesium.Math.toRadians(225);
    const offsetLat   = coords.latitude  - (OFFSET_DIST * Math.cos(bearing) / 111320);
    const offsetLon   = coords.longitude - (OFFSET_DIST * Math.sin(bearing) / (111320 * Math.cos(Cesium.Math.toRadians(coords.latitude))));
    const offsetPos   = Cesium.Cartesian3.fromDegrees(offsetLon, offsetLat, terrainHeight + OFFSET_H);
    const buildingPos = Cesium.Cartesian3.fromDegrees(coords.longitude, coords.latitude, terrainHeight);

    window.viewer.camera.flyTo({
        destination: offsetPos,
        duration: 2,
        complete: () => {
            const offset = Cesium.Cartesian3.subtract(window.viewer.camera.position, buildingPos, new Cesium.Cartesian3());
            window.viewer.camera.lookAt(buildingPos, offset);
            window.viewer.camera.lookAtTransform(Cesium.Matrix4.IDENTITY);
        }
    });
};

// ---------------------------------------------------------------------------
// Model drop + transform helper
// ---------------------------------------------------------------------------

/**
 * Drop the 3-D model into the scene after a short delay (to let the camera
 * settle), then apply scale / rotation.
 */
const loadModelAtTimeout = (terrainHeight, modelScale, modelRotation, coords, sceneData) => {
    // Reduced delay from 6000ms to 1000ms to improve perceived loading speed
    setTimeout(() => {
        window.destSelectedCenter = {
            latitude:  Cesium.Math.toRadians(coords.latitude),
            longitude: Cesium.Math.toRadians(coords.longitude),
            height:    terrainHeight
        };

        // Drop the model
        const tryDrop = (url) => {
            try {
                window.dropNamedAsset(url);
            } catch (err) {
                console.error('❌ Failed to drop asset:', err);
            }
        };

        // ── Snapshot the current drone reference BEFORE dropping the new model ──
        // App.js sets window.drone when the model entity is fully created on the
        // Cesium viewer. We poll for the reference to change — that is the real
        // "model is on screen" signal, independent of any fixed timeout.
        const prevDrone = window.drone ?? null;

        const startModelReadyPoll = () => {
            const POLL_INTERVAL_MS = 800;
            const MAX_POLL_MS      = 180_000; // 3 minutes — GLB blob with injected TEXCOORD_1 is larger
            const started          = Date.now();

            const poll = setInterval(() => {
                const elapsed = Date.now() - started;

                const droneChanged  = window.drone != null && window.drone !== prevDrone;
                const timedOut      = elapsed >= MAX_POLL_MS;
                const fetchFailed   = window._flattenGlbFailed === true;  // 403 / URL expired

                if (droneChanged || timedOut || fetchFailed) {
                    clearInterval(poll);

                    if (droneChanged) {
                        console.log('✅ window.drone updated — model is on screen, showing banner');
                    } else if (fetchFailed) {
                        console.warn('⚠️  Model load aborted — GLB fetch failed (presigned URL expired). Please reload the scene.');
                        window._flattenGlbFailed = false;
                    } else {
                        console.warn('⚠️  Model ready poll timed out after', MAX_POLL_MS / 1000, 's');
                    }

                    // Apply scale / rotation transforms now that the model is ready
                    if (modelScale && window.drone) {
                        window.drone.model.scale = modelScale;
                    }
                    // Compute correct ECEF orientation from angularRotation.
                    // We do NOT use the stored quaternion directly — it may have been saved in an
                    // older format where (0,0,1,0) = 180° around ECEF-Z, which causes ~113° tilt
                    // at non-polar latitudes.  headingPitchRollQuaternion converts local ENU
                    // heading → ECEF, ensuring the model stands upright on the globe surface.
                    if (window.drone) {
                        try {
                            const angularDeg = parseFloat(sceneData?.angularRotation) || 0;
                            const hpr        = new Cesium.HeadingPitchRoll(Cesium.Math.toRadians(angularDeg), 0, 0);
                            const modelPos   = Cesium.Cartesian3.fromDegrees(coords.longitude, coords.latitude, terrainHeight);
                            window.drone.orientation  = Cesium.Transforms.headingPitchRollQuaternion(modelPos, hpr);
                            window.drone.rotationAngle = angularDeg;
                        } catch (e) {
                            console.warn('⚠️ Failed to compute orientation from angularRotation:', e.message);
                        }
                    }

                    // Switch to 3-D, reposition UI chrome, then show "Model Loaded"
                    window.viewer.scene.mode = Cesium.SceneMode.SCENE3D;
                    
                    // Force Cesium to render the scene immediately
                    window.viewer.scene.requestRender();
                    
                    const twinApp = document.querySelector('twinworld-app');
                    if (twinApp?.shadowRoot) {
                        const compass  = twinApp.shadowRoot.querySelector('.compass');
                        const navCtrl  = twinApp.shadowRoot.querySelector('.navigation-controls');
                        if (compass)  compass.style.right = '100px';
                        if (navCtrl)  navCtrl.style.right = '130px';
                    }

                    showNotification('success', 'Scene loaded successfully!');
                    if (typeof window.setModelLoadingBanner === 'function') window.setModelLoadingBanner('loaded');
                    
                    // Force another render after a short delay to ensure visibility
                    setTimeout(() => {
                        if (window.viewer?.scene) {
                            window.viewer.scene.requestRender();
                        }
                    }, 100);
                    
                    // Notify the Gaussian Splat panel to refresh its list
                    window.dispatchEvent(new CustomEvent('twinworld:scene-loaded', { detail: { sceneId: window.sceneId } }));
                }
            }, POLL_INTERVAL_MS);
        };

        if (sceneData.buildingModel) {
            const path     = sceneData.buildingModel.replace('/twinworld/utils/downloads', '');
            // Only prepend CloudFront if path is not already an absolute URL
            const modelUrl = path.startsWith('http') ? path : `${getCloudfront()}${path}`;
            window.modelScale      = modelScale;
            // Do NOT pass the stored quaternion to App.js — it may be a raw ECEF
            // quaternion that was saved in building-app format and tilts the model
            // at non-polar latitudes. Orientation is applied correctly by the poll
            // below via headingPitchRollQuaternion(angularRotation).
            window.modelRotation   = null;
            window.angularRotation = sceneData.angularRotation ? parseFloat(sceneData.angularRotation) : 0;
            console.log('🔧 Model scale:', modelScale, 'rotation:', modelRotation, 'angularRotation:', window.angularRotation);
            tryDrop(modelUrl);
            startModelReadyPoll();
        } else if (sceneData.local_glb_uri) {
            tryDrop(sceneData.local_glb_uri);
            startModelReadyPoll();
        } else {
            console.warn('⚠️  No building model or local_glb_uri – scene loads without 3-D model');
            // No model to load — clear the loading banner immediately
            if (typeof window.setModelLoadingBanner === 'function') window.setModelLoadingBanner('loaded');
        }
    }, 1000); // Reduced from 6000ms to 1000ms for faster loading
};

// ---------------------------------------------------------------------------
// Bounding-box parser (inline – kept local to avoid circular deps)
// ---------------------------------------------------------------------------

const parseBoundingBoxLocal = (rawBbox, latFallback, lonFallback) => {
    try {
        if (!rawBbox) throw new Error('Bounding box data is missing');

        let bboxData;
        if (typeof rawBbox === 'string') {
            const trimmed = rawBbox.trim();
            if (trimmed.match(/^-?[\d.]+,-?[\d.]+,-?[\d.]+,-?[\d.]+$/)) {
                const parts = trimmed.split(',').map(parseFloat);
                bboxData = {
                    west:  Cesium.Math.toRadians(parts[0]),
                    south: Cesium.Math.toRadians(parts[1]),
                    east:  Cesium.Math.toRadians(parts[2]),
                    north: Cesium.Math.toRadians(parts[3])
                };
            } else if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                const parsed = JSON.parse(trimmed);
                bboxData = Array.isArray(parsed)
                    ? { west: parsed[0], south: parsed[1], east: parsed[2], north: parsed[3] }
                    : parsed;
            } else {
                throw new Error(`Unrecognized bbox format: ${trimmed}`);
            }
        } else if (typeof rawBbox === 'object') {
            bboxData = rawBbox;
        } else {
            throw new Error(`Invalid bbox type: ${typeof rawBbox}`);
        }

        if (bboxData.south >= bboxData.north) {
            [bboxData.south, bboxData.north] = [bboxData.north, bboxData.south];
        }

        window.destBoundingBoxStr = bboxData;
        window.destBoundingBox    = new Cesium.Rectangle(bboxData.west, bboxData.south, bboxData.east, bboxData.north);
        window.destCenter         = Cesium.Rectangle.center(window.destBoundingBox);

    } catch (bboxError) {
        console.error('❌ Failed to parse bounding box:', bboxError.message, rawBbox);

        const lat = parseFloat(latFallback);
        const lon = parseFloat(lonFallback);
        if (isNaN(lat) || isNaN(lon)) {
            throw new Error('Invalid scene coordinates – cannot create fallback bbox');
        }

        const latRad = Cesium.Math.toRadians(lat);
        const lonRad = Cesium.Math.toRadians(lon);
        const delta  = 0.001;

        window.destBoundingBox    = new Cesium.Rectangle(lonRad - delta, latRad - delta, lonRad + delta, latRad + delta);
        window.destBoundingBoxStr = { west: lonRad - delta, south: latRad - delta, east: lonRad + delta, north: latRad + delta };
        window.destCenter         = Cesium.Rectangle.center(window.destBoundingBox);
        console.warn('⚠️  Fallback bbox created from scene coords');
    }
};

// ---------------------------------------------------------------------------
// window.loadScene
// ---------------------------------------------------------------------------

window.loadScene = (scene) => {
    console.log('🏗️  PHASE 3: window.loadScene()', new Date().toISOString());
    console.log('📦 Input Scene Data:', JSON.stringify(scene, null, 2));

    window.scenetype = 'default';
    hideITwinHeightSlider();

    // Wait for viewer if not yet ready
    if (!window.viewer || !window.viewer.camera) {
        console.warn('⏳ Viewer not ready – retrying after init…');
        waitForViewerReady()
            .then(() => window.loadScene(scene))
            .catch(err => console.error('❌ Failed to initialise viewer:', err));
        return;
    }

    showNotification('loading', 'Loading scene, please wait...');
    if (typeof window.setModelLoadingBanner === 'function') window.setModelLoadingBanner('loading');

    const twinProvider = scene.twinProvider || 'cesium';

    // ── iTwin provider ──────────────────────────────────────────────────────
    if (twinProvider === 'itwin') {
        const validation = validateITwinConfig(scene.itwinConfig);
        if (!validation.valid) {
            const msg = `Invalid iTwin configuration: ${validation.errors.join(', ')}`;
            console.error('❌', msg);
            showNotification('error', msg);
            return;
        }
        setTimeout(() => {
            window.loadITwinScene(scene.itwinConfig, scene)
                .then(() => showNotification('success', `iTwin scene loaded: ${scene.name}`))
                .catch(err => {
                    console.error('Failed to load iTwin scene:', err);
                    showNotification('error', `Failed to load iTwin scene: ${err.message}`);
                });
        }, 1000);
        return;
    }

    // ── Standard Cesium provider ────────────────────────────────────────────
    const dataValue = { ...scene };

    // Normalise buildingModel path
    if (dataValue.buildingModel?.startsWith(getBaseUrl())) {
        dataValue.buildingModel = dataValue.buildingModel.replace(getBaseUrl(), '');
    }
    if (dataValue.buildingModel?.startsWith('s3://')) {
        const cloudfrontBase = getCloudfront();
        dataValue.buildingModel = dataValue.buildingModel
            .replace('s3://dev-translated-models',  cloudfrontBase)
            .replace('s3://prod-translated-models', cloudfrontBase);
    }

    // Parse bounding box
    try {
        parseBoundingBoxLocal(dataValue.boundingBox, dataValue.latitude, dataValue.longitude);
    } catch (err) {
        console.error(err.message);
        showNotification('error', 'Invalid scene coordinates. Cannot load scene.');
        return;
    }

    // Parse rotation & scale
    if (typeof dataValue.rotation === 'string' && dataValue.rotation.match('^[0-9.,-]+$')) {
        dataValue.rotation = dataValue.rotation.split(',').map(parseFloat);
    }

    // Helper: convert any quaternion representation → Cesium.Quaternion (or null)
    const parseQuaternion = (raw) => {
        if (!raw) return null;
        try {
            // Case 1: already a Cesium.Quaternion or plain {x,y,z,w} object
            if (typeof raw === 'object' && !Array.isArray(raw) &&
                raw.x !== undefined && raw.y !== undefined &&
                raw.z !== undefined && raw.w !== undefined) {
                return new Cesium.Quaternion(raw.x, raw.y, raw.z, raw.w);
            }
            // Case 2: JSON string → parse then recurse
            if (typeof raw === 'string') {
                // Handle comma-separated "x,y,z,w" format (e.g. "0.0,0.0,1.0,6.12e-17")
                if (raw.includes(',') && !raw.includes('{') && !raw.includes('[')) {
                    const parts = raw.split(',').map(Number);
                    if (parts.length === 4 && parts.every(n => !isNaN(n))) {
                        return new Cesium.Quaternion(parts[0], parts[1], parts[2], parts[3]);
                    }
                }
                const p = JSON.parse(raw);
                return parseQuaternion(p);
            }
            // Case 3: array [x,y,z,w]
            if (Array.isArray(raw) && raw.length === 4) {
                return new Cesium.Quaternion(raw[0], raw[1], raw[2], raw[3]);
            }
        } catch (e) {
            console.warn('⚠️ Failed to parse rotation quaternion:', e);
        }
        return null;
    };

    // Only use rotation if it resolves to a valid quaternion — bare floats are angularRotation only
    const rotation = parseQuaternion(dataValue.rotation);
    const scale = dataValue.scale ? parseFloat(dataValue.scale) : null;

    const coords = {
        latitude:  parseFloat(dataValue.latitude),
        longitude: parseFloat(dataValue.longitude)
    };
    window.sceneId = dataValue.id;

    // Reset POI state
    window.allPickedAssets      = [];
    window.entityList           = [];
    window.poiFlagPoints        = [];
    window._poisLoadedForScene  = false;

    window.scanState    = 'complete';
    window.activeOption = 'contextData';
    window.destSelectedCenter = { latitude: coords.latitude, longitude: coords.longitude, height: parseFloat(dataValue.height) };

    // Fetch terrain elevation, fly camera, drop model — then click contextData AFTER model is placed
    setTimeout(() => {
    axios
        .get(`${getBaseUrl()}/twinworld/elevation?locations=${coords.latitude},${coords.longitude}`)
        .then((res) => {
            const rawHeight = res.data?.results?.[0]?.elevation;
            // Sanity check: elevation should be between -500m and 9000m for real locations.
            // Do NOT fall back to dataValue.height — that is building height, not terrain.
            const terrainHeight = (typeof rawHeight === 'number' && rawHeight > -500 && rawHeight < 9000)
                ? rawHeight
                : 0;
            window.destinationSeaLevelHeight = terrainHeight;
            flyCamera(coords, terrainHeight);
            loadModelAtTimeout(terrainHeight, scale, rotation, coords, dataValue);
            // Open contextData panel — now safe since #buildingMetricsVisualizer crash was removed from reducer
            setTimeout(() => {
                try {
                    const sr = document.querySelector('twinworld-app')?.shadowRoot;
                    if (!sr) return;
                    const contextBtn = sr.getElementById('contextData');
                    if (contextBtn) contextBtn.click();
                } catch (e) { console.warn('contextData click failed:', e.message); }
            }, 2000);
        })
        .catch((err) => {
            console.error('❌ Failed to fetch elevation:', err.message);
            // dataValue.height is building HEIGHT (e.g. 120m for a 120m-tall building),
            // NOT terrain elevation. Never use it as a terrain fallback — it places the
            // model underground at high-altitude locations (e.g. Phoenix terrain ~340m,
            // building height "120" → model 220m underground).
            // Instead, sample the currently-loaded Cesium globe terrain.
            let fallbackHeight = 0;
            try {
                const carto = Cesium.Cartographic.fromDegrees(coords.longitude, coords.latitude);
                const sampled = window.viewer?.scene?.globe?.getHeight(carto);
                if (typeof sampled === 'number' && sampled > -500 && sampled < 9000) {
                    fallbackHeight = sampled;
                }
            } catch (_) { /* globe not ready — use 0 (sea level) */ }
            window.destinationSeaLevelHeight = fallbackHeight;
            flyCamera(coords, fallbackHeight);
            loadModelAtTimeout(fallbackHeight, scale, rotation, coords, dataValue);
            setTimeout(() => {
                try {
                    const sr = document.querySelector('twinworld-app')?.shadowRoot;
                    if (!sr) return;
                    const contextBtn = sr.getElementById('contextData');
                    if (contextBtn) contextBtn.click();
                } catch (e) { console.warn('contextData click failed:', e.message); }
            }, 2000);
        });
    }, 1000);
};
